import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { updateProductImage } from '../../api/business/endpoints';
import type { AvailableLanguage, GalleryImage, ProductDetail } from '../../api/business/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { altChanges } from '../../components/gallery';
import { LanguageTabs } from '../../components/LanguageTabs';
import { languageName } from '../../components/localisedText';
import { isVariable, variantLabel } from '../../components/variants';
import { useLanguage } from '../../i18n/LanguageContext';

type GalleryImageDialogProps = {
  companyId: string;
  product: ProductDetail;
  photo: GalleryImage;
  /** The tenant's content languages, the default first. */
  languages: Array<string>;
  available: Array<AvailableLanguage>;
  onClose: () => void;
};

const ALT_MAX_LENGTH = 255;

/** A photo's alt text in every content language and, on a product with variants, the variants it shows. */
export const GalleryImageDialog = ({
  companyId,
  product,
  photo,
  languages,
  available,
  onClose,
}: GalleryImageDialogProps) => {
  const { language, t } = useLanguage();
  const [active, setActive] = useState(languages[0] ?? 'sk');
  const [alt, setAlt] = useState<Record<string, string>>(
    Object.fromEntries(languages.map((code) => [code, photo.alt[code] ?? ''])),
  );
  const [variantIds, setVariantIds] = useState<Array<string>>(photo.variantIds);
  const save = useAppMutation(
    () =>
      updateProductImage(companyId, product.id, photo.id, {
        alt: altChanges(photo.alt, alt),
        ...(isVariable(product) ? { variantIds } : {}),
      }),
    ['product-images', 'product'],
    onClose,
  );
  const current = languages.includes(active) ? active : languages[0];
  const variants = product.variants.filter((variant) => variant.optionValues.length > 0);

  return (
    <FormDialog
      title={t('gallery.editTitle')}
      onClose={onClose}
      onSubmit={() => save.mutate(undefined)}
      isBusy={save.isPending}
      error={save.error}
    >
      <Box
        component="img"
        src={photo.urls.small.webp}
        alt={photo.alt[languages[0]] ?? ''}
        sx={{ width: 160, height: 160, objectFit: 'contain', borderRadius: 1, bgcolor: 'action.hover' }}
      />
      <Stack spacing={1}>
        {languages.length > 1 ? (
          <LanguageTabs
            languages={languages}
            value={current}
            onChange={setActive}
            missing={(code) => (alt[code] ?? '').trim() === ''}
          />
        ) : null}
        <TextField
          fullWidth
          label={
            languages.length > 1
              ? `${t('gallery.alt')} (${languageName(current, available, language)})`
              : t('gallery.alt')
          }
          helperText={t('gallery.altHint')}
          value={alt[current] ?? ''}
          inputProps={{ maxLength: ALT_MAX_LENGTH }}
          onChange={(event) => setAlt({ ...alt, [current]: event.target.value })}
        />
      </Stack>
      {isVariable(product) ? (
        <Box>
          <FormLabel component="legend">{t('gallery.variants')}</FormLabel>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {t('gallery.variantsHint')}
          </Typography>
          <FormGroup>
            {variants.map((variant) => (
              <FormControlLabel
                key={variant.id}
                control={
                  <Checkbox
                    size="small"
                    checked={variantIds.includes(variant.id)}
                    onChange={(event) =>
                      setVariantIds(
                        event.target.checked
                          ? [...variantIds, variant.id]
                          : variantIds.filter((id) => id !== variant.id),
                      )
                    }
                  />
                }
                label={variantLabel(variant, t)}
              />
            ))}
          </FormGroup>
        </Box>
      ) : null}
    </FormDialog>
  );
};
